import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Film, Tv, Star, Menu } from "lucide-react";
import { SearchMovie, SearchTvShows, TrendingMovies, nowPlayingMovies, nowPlayingTvShows, allMoviesAPI, allSeriesAPI } from "../constants/api";
import { useSidebar } from "../contexts/SidebarContext";

const Navbar = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [trendingSearches, setTrendingSearches] = useState([]);
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [navbarLeft, setNavbarLeft] = useState(0);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const navbarRef = useRef(null);
  const location = useLocation();
  const router = useNavigate();
  const { isCollapsed, toggleMobile } = useSidebar();

  // Update navbar position based on sidebar state
  useEffect(() => {
    const updateNavbarPosition = () => {
      if (window.innerWidth > 1000) {
        // Desktop: adjust based on collapsed state
        setNavbarLeft(isCollapsed ? 80 : 256);
      } else {
        // Mobile/Tablet: sidebar is hidden, navbar starts at 0
        setNavbarLeft(0);
      }
    };

    updateNavbarPosition();
    const handleResize = () => updateNavbarPosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed]);

  // Update navbar width dynamically
  useEffect(() => {
    const updateNavbarWidth = () => {
      if (navbarRef.current) {
        if (window.innerWidth >= 1024) {
          navbarRef.current.style.width = `calc(100% - ${navbarLeft}px)`;
        } else {
          navbarRef.current.style.width = '100%';
        }
      }
    };

    updateNavbarWidth();
    window.addEventListener('resize', updateNavbarWidth);
    return () => window.removeEventListener('resize', updateNavbarWidth);
  }, [navbarLeft]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setSearchOpen(false);
  }, [location]);

  // Fetch trending searches when search is focused and empty
  useEffect(() => {
    const fetchTrendingSearches = async () => {
      if ((searchFocused || searchOpen) && !searchTerm.trim()) {
        try {
          // Fetch from multiple sources for maximum variety
          const [trendingData, moviesData, popularMoviesData, tvShowsData, topRatedTvData] = await Promise.all([
            TrendingMovies(),
            nowPlayingMovies(),
            allMoviesAPI(1),
            nowPlayingTvShows(),
            allSeriesAPI(1),
          ]);

          // Extract titles from all sources
          const trendingTitles = (trendingData?.results || [])
            .map(item => item.title || item.name)
            .filter(title => title && title.trim());
          
          const movieTitles = (moviesData?.results || [])
            .map(item => item.title)
            .filter(title => title && title.trim());
          
          const popularMovieTitles = (popularMoviesData?.results || [])
            .map(item => item.title)
            .filter(title => title && title.trim());
          
          const tvShowTitles = (tvShowsData?.results || [])
            .map(item => item.name)
            .filter(name => name && name.trim());
          
          const topRatedTvTitles = (topRatedTvData?.results || [])
            .map(item => item.name)
            .filter(name => name && name.trim());

          // Combine all titles from different sources
          const allTitles = [
            ...trendingTitles,
            ...movieTitles,
            ...popularMovieTitles,
            ...tvShowTitles,
            ...topRatedTvTitles,
          ];
          
          // Remove duplicates
          const uniqueTitles = [...new Set(allTitles)];
          
          // Shuffle array to mix movies and TV shows
          const shuffled = uniqueTitles.sort(() => Math.random() - 0.5);
          
          // Show up to 12 trending searches
          setTrendingSearches(shuffled.slice(0, 12));
        } catch (error) {
          console.error("Error fetching trending searches:", error);
        }
      } else {
        setTrendingSearches([]);
      }
    };

    fetchTrendingSearches();
  }, [searchFocused, searchTerm, searchOpen]);

  // Autocomplete search with debouncing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.trim().length >= 2) {
      setLoadingAutocomplete(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const [movieResults, tvResults] = await Promise.all([
            SearchMovie(searchTerm.trim()),
            SearchTvShows(searchTerm.trim()),
          ]);

          const combined = [
            ...(movieResults?.results || []).slice(0, 5).map(item => ({ ...item, media_type: 'movie' })),
            ...(tvResults?.results || []).slice(0, 5).map(item => ({ ...item, media_type: 'tv' })),
          ];

          setAutocompleteResults(combined.slice(0, 10));
        } catch (error) {
          console.error("Error fetching autocomplete:", error);
        } finally {
          setLoadingAutocomplete(false);
        }
      }, 300);
    } else {
      setAutocompleteResults([]);
      setLoadingAutocomplete(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Show dropdown when focused and has trending searches or autocomplete results
  useEffect(() => {
    setShowDropdown((searchFocused || searchOpen) && (trendingSearches.length > 0 || autocompleteResults.length > 0 || loadingAutocomplete));
  }, [searchFocused, searchOpen, trendingSearches, autocompleteResults, loadingAutocomplete]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        const searchInput = event.target.closest('input[type="text"]');
        if (!searchInput) {
          setShowDropdown(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = (event) => {
    event?.preventDefault();
    if (searchTerm.trim()) {
      router(`/search/${searchTerm}`);
      setSearchTerm("");
      setSearchFocused(false);
      setShowDropdown(false);
    }
  };

  const handleItemClick = (item) => {
    const route = item.media_type === 'tv' ? `/tvshows/${item.id}` : `/${item.id}`;
    router(route);
    setSearchTerm("");
    setSearchFocused(false);
    setShowDropdown(false);
  };

  const handleTrendingSearchClick = (searchTerm) => {
    router(`/search/${encodeURIComponent(searchTerm)}`);
    setSearchTerm("");
    setSearchFocused(false);
    setShowDropdown(false);
  };

  const isActive = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <>
      <section
        ref={navbarRef}
        className={`fixed top-0 right-0 z-[10000] transition-all duration-300 ${
          scrolled
            ? "bg-black/95 backdrop-blur-md shadow-lg shadow-black/50"
            : "bg-gradient-to-b from-black via-black/95 to-transparent"
        }`}
        style={{ 
          left: `${navbarLeft}px`,
          transition: 'left 0.3s ease-in-out'
        }}
      >
        <nav className="paddingNav py-4 flex items-center justify-between relative">
          {/* Mobile Logo */}
          <Link to={"/"} className="lg:hidden flex items-center group">
            <h1 className="cursor-pointer text-red font-bold poppins text-xl tracking-tight transition-all duration-300 group-hover:scale-105">
              FilmJetHub
            </h1>
          </Link>

          {/* Desktop Navigation - Right Aligned */}
          <div className="hidden lg:flex items-center gap-[2rem] ml-auto">
            {/* Search Bar */}
            <div className="relative" ref={dropdownRef}>
              <form
                onSubmit={handleSubmit}
                className={`relative flex items-center transition-all duration-300 ${
                  searchFocused ? "w-[420px]" : "w-[380px]"
                }`}
              >
                <input
                  className="w-full h-[42px] pl-12 pr-4 outline-none border border-white/20 bg-white/5 backdrop-blur-sm rounded-full text-white text-[15px] mont placeholder:text-white/50 transition-all duration-300 focus:border-red focus:bg-white/10 focus:w-[420px]"
                  type="text"
                  placeholder="Search movies, TV shows, anime..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => {
                    // Delay blur to allow click on dropdown items
                    setTimeout(() => {
                      if (!dropdownRef.current?.contains(document.activeElement)) {
                        setSearchFocused(false);
                      }
                    }, 200);
                  }}
                />
                <button
                  type="submit"
                  className="absolute left-3 cursor-pointer transition-transform duration-300 hover:scale-110 active:scale-95 text-white/70 hover:text-white"
                >
                  <Search size={20} />
                </button>
              </form>

              {/* Search Dropdown */}
              {showDropdown && (
                <div className="absolute top-full left-0 mt-2 w-[420px] bg-black border border-white/10 rounded-lg shadow-2xl max-h-[500px] overflow-y-auto search-dropdown z-[10001] slide-bottom">
                  {!searchTerm.trim() && trendingSearches.length > 0 && (
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="h-1 w-8 bg-red"></div>
                        <h3 className="text-white poppins font-semibold text-sm">Trending Searches</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {trendingSearches.map((search, index) => (
                          <button
                            key={index}
                            onClick={() => handleTrendingSearchClick(search)}
                            className="px-3 py-1.5 bg-gray/20 hover:bg-gray/30 border border-white/10 hover:border-red/50 rounded-full text-white mont text-xs transition-all duration-200 hover:text-red group"
                          >
                            <span className="flex items-center gap-1.5">
                              <Search size={12} className="text-white/60 group-hover:text-red transition-colors" />
                              {search}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {searchTerm.trim() && (
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-1 w-8 bg-red"></div>
                        <h3 className="text-white poppins font-semibold text-sm">Search Results</h3>
                      </div>
                      {loadingAutocomplete ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red"></div>
                        </div>
                      ) : autocompleteResults.length > 0 ? (
                        <div className="space-y-2">
                          {autocompleteResults.map((item) => (
                            <div
                              key={`${item.media_type}-${item.id}`}
                              onClick={() => handleItemClick(item)}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray/20 cursor-pointer transition-all duration-200 group"
                            >
                              <div className="w-12 h-16 relative flex-shrink-0">
                                {item.poster_path ? (
                                  <img
                                    src={`https://image.tmdb.org/t/p/w92${item.poster_path}`}
                                    alt={item.title || item.name}
                                    className="w-full h-full object-cover rounded border border-white/10 group-hover:border-red/50 transition-colors"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const fallback = e.target.parentElement.querySelector('.poster-fallback');
                                      if (fallback) {
                                        fallback.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : null}
                                <div className={`poster-fallback w-full h-full bg-gray/30 rounded border border-white/10 flex items-center justify-center group-hover:border-red/50 transition-colors ${item.poster_path ? 'hidden' : 'flex'} absolute inset-0`}>
                                  {item.media_type === 'tv' ? (
                                    <Tv className="text-white/40" size={20} />
                                  ) : (
                                    <Film className="text-white/40" size={20} />
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-white mont text-sm font-medium truncate group-hover:text-red transition-colors">
                                    {item.title || item.name}
                                  </p>
                                  <span className={`text-xs font-medium mont flex-shrink-0 ${
                                    item.media_type === 'tv' 
                                      ? 'text-blue-500' 
                                      : 'text-red'
                                  }`}>
                                    {item.media_type === 'tv' ? 'TV Show' : 'Movie'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  {item.media_type === 'tv' ? (
                                    <Tv className="text-white/40" size={12} />
                                  ) : (
                                    <Film className="text-white/40" size={12} />
                                  )}
                                  {item.vote_average && (
                                    <div className="flex items-center gap-1">
                                      <Star className="text-yellow-400" size={10} fill="#FBBF24" />
                                      <span className="text-white/60 mont text-xs">{item.vote_average.toFixed(1)}</span>
                                    </div>
                                  )}
                                  {item.release_date && (
                                    <span className="text-white/40 mont text-xs">
                                      {new Date(item.release_date).getFullYear()}
                                    </span>
                                  )}
                                  {item.first_air_date && (
                                    <span className="text-white/40 mont text-xs">
                                      {new Date(item.first_air_date).getFullYear()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="py-8 text-center">
                          <p className="text-white/60 mont text-sm">No results found</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Download Button */}
            <Link
              to={"/download"}
              className="text-white mont font-medium lg:text-[17px] text-[16px] transition-all duration-300 hover:text-red"
            >
              Download App
            </Link>
          </div>

          {/* Mobile Icons */}
          <div className="flex items-center gap-4 lg:hidden">
            {/* Search Toggle Button */}
            <button
              onClick={() => {
                setSearchOpen(!searchOpen);
                if (!searchOpen) {
                  setTimeout(() => setSearchFocused(true), 100);
                }
              }}
              className="relative p-2 transition-transform duration-300 hover:scale-110 active:scale-95 text-white"
              aria-label="Toggle search"
            >
              <Search size={24} />
            </button>
            
            {/* Menu Toggle Button */}
            <button
              onClick={toggleMobile}
              className="relative p-2 transition-transform duration-300 hover:scale-110 active:scale-95 text-white"
              aria-label="Toggle menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </nav>

        {/* Mobile Search Dropdown */}
        {searchOpen && (
          <section className="lg:hidden px-4 py-4 bg-black/98 backdrop-blur-md border-t border-white/10 slide-bottom">
            <div className="relative">
              <form
                onSubmit={handleSubmit}
                className="flex relative items-center"
              >
                <input
                  className="w-full h-[45px] pl-12 pr-4 outline-none border border-white/20 bg-white/5 backdrop-blur-sm rounded-full text-white text-[16px] mont placeholder:text-white/50 transition-all duration-300 focus:border-red focus:bg-white/10"
                  type="text"
                  placeholder="Search movies, TV shows, anime..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  autoFocus
                />
                <button
                  type="submit"
                  className="absolute left-3 cursor-pointer transition-transform duration-300 hover:scale-110 active:scale-95 text-white/70 hover:text-white"
                >
                  <Search size={20} />
                </button>
              </form>

              {/* Mobile Search Dropdown */}
              {showDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-black border border-white/30 rounded-lg shadow-2xl max-h-[400px] overflow-y-auto search-dropdown z-[10001] slide-bottom">
                  {!searchTerm.trim() && trendingSearches.length > 0 && (
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="h-1 w-8 bg-red"></div>
                        <h3 className="text-white poppins font-semibold text-sm">Trending Searches</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {trendingSearches.map((search, index) => (
                          <button
                            key={index}
                            onClick={() => handleTrendingSearchClick(search)}
                            className="px-3 py-1.5 bg-black/70 backdrop-blur-sm hover:bg-black/90 border border-white/30 hover:border-red/50 rounded-full text-white mont text-xs transition-all duration-200 hover:text-red group shadow-lg"
                          >
                            <span className="flex items-center gap-1.5">
                              <Search size={12} className="text-white/80 group-hover:text-red transition-colors" />
                              {search}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {searchTerm.trim() && (
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-1 w-8 bg-red"></div>
                        <h3 className="text-white poppins font-semibold text-sm">Search Results</h3>
                      </div>
                      {loadingAutocomplete ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red"></div>
                        </div>
                      ) : autocompleteResults.length > 0 ? (
                        <div className="space-y-2">
                          {autocompleteResults.map((item) => (
                            <div
                              key={`${item.media_type}-${item.id}`}
                              onClick={() => handleItemClick(item)}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray/20 cursor-pointer transition-all duration-200 group"
                            >
                              <div className="w-12 h-16 relative flex-shrink-0">
                                {item.poster_path ? (
                                  <img
                                    src={`https://image.tmdb.org/t/p/w92${item.poster_path}`}
                                    alt={item.title || item.name}
                                    className="w-full h-full object-cover rounded border border-white/10 group-hover:border-red/50 transition-colors"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const fallback = e.target.parentElement.querySelector('.poster-fallback');
                                      if (fallback) {
                                        fallback.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : null}
                                <div className={`poster-fallback w-full h-full bg-gray/30 rounded border border-white/10 flex items-center justify-center group-hover:border-red/50 transition-colors ${item.poster_path ? 'hidden' : 'flex'} absolute inset-0`}>
                                  {item.media_type === 'tv' ? (
                                    <Tv className="text-white/40" size={20} />
                                  ) : (
                                    <Film className="text-white/40" size={20} />
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-white mont text-sm font-medium truncate group-hover:text-red transition-colors">
                                    {item.title || item.name}
                                  </p>
                                  <span className={`text-xs font-medium mont flex-shrink-0 ${
                                    item.media_type === 'tv' 
                                      ? 'text-blue-500' 
                                      : 'text-red'
                                  }`}>
                                    {item.media_type === 'tv' ? 'TV Show' : 'Movie'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  {item.media_type === 'tv' ? (
                                    <Tv className="text-white/40" size={12} />
                                  ) : (
                                    <Film className="text-white/40" size={12} />
                                  )}
                                  {item.vote_average && (
                                    <div className="flex items-center gap-1">
                                      <Star className="text-yellow-400" size={10} fill="#FBBF24" />
                                      <span className="text-white/60 mont text-xs">{item.vote_average.toFixed(1)}</span>
                                    </div>
                                  )}
                                  {item.release_date && (
                                    <span className="text-white/40 mont text-xs">
                                      {new Date(item.release_date).getFullYear()}
                                    </span>
                                  )}
                                  {item.first_air_date && (
                                    <span className="text-white/40 mont text-xs">
                                      {new Date(item.first_air_date).getFullYear()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="py-8 text-center">
                          <p className="text-white/60 mont text-sm">No results found</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

      </section>

    </>
  );
};

export default Navbar;
